import { Component, ElementRef, inject, Input, OnInit, signal, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { PlayerMessageService } from '../../../core/services/player.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { GroupedCompetencies, PlayerTheme } from '../../consumer.entity';
import { defaultTheme, fonts } from '../../../core/types';
import { SubjectJsonModel } from '../../../subject/subject.json.model';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ColorPickerDialogComponent } from '../../../core/components/color-picker/color-picker-dialog.component';

interface SubjectConfig {
  subjectId: string
  fontId: string
}

@Component({
  selector: 'pixl-consumer-player-config',
  templateUrl: './consumer-player-config.component.html',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatCheckbox,
    MatButton,
    MatTooltip,
    TranslocoModule,
  ],
  standalone: true,
  styleUrls: ['../form-dialog-consumer.component.scss']
})
export class ConsumerPlayerConfigComponent implements OnInit {
  @Input() parentForm!: FormGroup;
  @Input() consumerId?: string;
  @Input() resetTheme!: PlayerTheme;
  @Input() competencies: SubjectJsonModel[] = [];

  @ViewChild('playerPreviewIframe') playerPreviewIframe!: ElementRef<HTMLIFrameElement>;

  private playerService = inject(PlayerMessageService);
  private sanitizer = inject(DomSanitizer);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private translocoService = inject(TranslocoService);

  public iframePreviewUrl!: SafeResourceUrl;
  public disabledColorSchemeBtn = signal(true);
  public groupedCompetencies: GroupedCompetencies[] = [];

  ngOnInit(): void {
    this.createResetTheme();

    if (this.consumerId) {
      const unsafeUrl = this.playerService.theme2PlayerPreviewUrl(this.consumerId);
      this.iframePreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(unsafeUrl);
    }

    this.groupCompetenciesByFirstLetter();
  }

  private createResetTheme(): void {
    const themeFormGroupValue = this.parentForm?.get('playerSettings.theme')?.value as PlayerTheme | null;
    this.resetTheme = { ...defaultTheme, ...themeFormGroupValue };
  }

  public setDefaultTheme(): void {
    const themeFormGroup = this.parentForm.get('playerSettings.theme') as FormGroup;
    if (themeFormGroup) {
      themeFormGroup.patchValue(this.resetTheme);
      this.sendThemeToPlayer();
      this.cdr.markForCheck();
    }
    this.disabledColorSchemeBtn.set(true);
  }

  public sendThemeToPlayer(): void {
    if (!this.playerService.iFrameReady) return;
    const themeColors = this.parentForm.get('playerSettings.theme')?.value as PlayerTheme | null;
    if (!themeColors) return;

    this.disabledColorSchemeBtn.set(false);

    const message = {
      themePreview: {
        themeColors: { ...themeColors }
      }
    };

    this.playerService.postMessageToPlayer(
      this.playerService.theme2PlayerPreviewUrl(this.consumerId || ''),
      message
    );
  }

  public onIframeLoaded(): void {
    if (!this.consumerId) return;

    this.playerService.iFrameReady = true;
    this.playerService.playerPreviewIFrame = this.playerPreviewIframe;

    this.playerService.postMessageToPlayer(
      this.playerService.theme2PlayerPreviewUrl(this.consumerId),
      { themePreview: 'redaInitIframe' }
    );

    this.sendThemeToPlayer();
  }

  private groupCompetenciesByFirstLetter(): void {
    const grouped: Record<string, SubjectJsonModel[]> = {};
    const sorted = [...this.competencies].sort((a, b) => a.name.localeCompare(b.name));

    sorted.forEach(comp => {
      const letter = comp.name ? comp.name.charAt(0).toUpperCase() : '#';
      if (!grouped[letter]) grouped[letter] = [];
      grouped[letter].push(comp);
    });

    this.groupedCompetencies = Object.keys(grouped)
      .sort()
      .map(letter => ({ letter, competencies: grouped[letter] }));
  }

  public getFont(competency: SubjectJsonModel): string {
    const subjectConfigs = (this.parentForm.get('playerSettings.subjectConfigs')?.value || []) as SubjectConfig[];
    const config = subjectConfigs.find((c: SubjectConfig) => c.subjectId === competency.treeId);
    return config?.fontId ?? 'def';
  }

  public setFont(fontId: string, competency: SubjectJsonModel): void {
    const subjectConfigsFormArray = this.parentForm.get('playerSettings.subjectConfigs') as FormArray;
    if (!subjectConfigsFormArray) return;

    const index = (subjectConfigsFormArray.value as SubjectConfig[]).findIndex((c: SubjectConfig) => c.subjectId === competency.treeId);
    if (index !== -1) {
      subjectConfigsFormArray.at(index).get('fontId')?.setValue(fontId);
    }
  }

  public getColorValue(controlName: string): string {
    return (this.parentForm.get(`playerSettings.theme.${controlName}`)?.value as string) || '#000000';
  }

  public onColorPickerChange(color: string, controlName: string): void {
    const control = this.parentForm.get(`playerSettings.theme.${controlName}`);
    if (control) {
      control.setValue(color, { emitEvent: false });
      this.sendThemeToPlayer();
      this.cdr.markForCheck();
    }
  }

  public onColorPickerSelect(): void {
    this.sendThemeToPlayer();
    this.cdr.markForCheck();
  }

  public openColorPicker(controlName: string, translationKey: string): void {
    const currentColor = this.getColorValue(controlName);
    const translatedTitle = this.translocoService.translate(translationKey);
    const dialogRef = this.dialog.open(ColorPickerDialogComponent, {
      width: '360px',
      disableClose: true,
      data: {
        initialColor: currentColor,
        title: translatedTitle
      }
    });

    dialogRef.afterClosed().subscribe((newColor: string | undefined) => {
      if (newColor) {
        this.onColorPickerChange(newColor, controlName);
        this.onColorPickerSelect();
      }
    });
  }

  protected readonly availableFonts = fonts;
}
